import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { Newspaper, Search, Loader2, X, PenSquare, Sparkles } from 'lucide-react';
import { NewsSkeleton } from '../Common/Skeleton';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { fetchSupabaseNewsList } from '../../services/footballService';
import { deleteNewsPermanently } from '../../services/adminService';
import { supabase } from '../../services/authService';
import { NewsItem } from '../../types';
import { t } from '../../i18n';
import { XlevenLogo } from '../Common/XlevenLogo';
import { SearchOverlay } from '../Home/SearchOverlay';
import { ConfirmModal } from '../Common/ConfirmModal';
import { ReportModal } from '../Common/ReportModal';
import { FeedNewsCard } from '../News/FeedNewsCard';
import { DateStrip } from '../News/DateStrip';
import { clearCache } from '../../services/storageService';
import { SwipeTutorialOverlay } from '../News/SwipeTutorialOverlay';
import SEO from '../Common/SEO';
import AdBanner from '../Common/AdBanner';

interface NewsViewProps {
    selectedDateFromUrl?: string;
    onNavigateToNews: (item: NewsItem) => void;
    onDateChange: (date: string | null) => void;
    onOpenSidebar: () => void;
}

const INITIAL_BATCH_SIZE = 20; 

const formatLocalDate = (date: Date) => {
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().split('T')[0];
};

const isValidUuid = (uuid: string) => {
    const re = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return re.test(uuid);
};

export const NewsView: React.FC<NewsViewProps> = ({ selectedDateFromUrl, onNavigateToNews, onDateChange, onOpenSidebar }) => {
    const { lang } = useLanguage();
    const { user, isLoading: isAuthLoading } = useAuth();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [news, setNews] = useState<NewsItem[] | null>(null);
    const [prevNews, setPrevNews] = useState<NewsItem[]>([]);
    const [nextNews, setNextNews] = useState<NewsItem[]>([]);
    const newsCache = useRef<Record<string, { items: NewsItem[], nextPage: number | undefined }>>({});
    const [nextPage, setNextPage] = useState<number | undefined>(0);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [showTutorial, setShowTutorial] = useState(false);

    // Swipe state
    const touchStartX = useRef<number | null>(null);
    const touchStartY = useRef<number | null>(null);
    const MIN_SWIPE_DISTANCE = 50;

    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [activeNewsId, setActiveNewsId] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isReporting, setIsReporting] = useState(false);
    const [direction, setDirection] = useState(0); // 1: left, -1: right

    const scrollToTop = () => {
        setTimeout(() => {
            const mainElement = document.querySelector('main');
            if (mainElement) {
                mainElement.scrollTo({ top: 0, behavior: 'auto' });
                if (mainElement.firstElementChild) {
                    mainElement.firstElementChild.scrollTo({ top: 0, behavior: 'auto' });
                }
            }
            window.scrollTo({ top: 0, behavior: 'auto' });
        }, 80);
    };

    const prevDateRef = useRef(selectedDateFromUrl);

    const observerTarget = useRef(null);
    const effectiveTodayStr = useMemo(() => formatLocalDate(new Date()), []);

    const availableDates = useMemo(() => {
        const dates = [];
        const baseDate = new Date();
        for (let i = 0; i < 30; i++) {
            const d = new Date(baseDate);
            d.setDate(baseDate.getDate() - i);
            dates.push({
                full: formatLocalDate(d),
                day: d.getDate(),
                weekday: d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
                month: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
            });
        }
        return dates;
    }, []);

    useEffect(() => {
        if (!selectedDateFromUrl) {
            onDateChange(effectiveTodayStr);
        }

        if (selectedDateFromUrl && prevDateRef.current && prevDateRef.current !== selectedDateFromUrl) {
            const current = new Date(selectedDateFromUrl).getTime();
            const prev = new Date(prevDateRef.current).getTime();
            setDirection(current < prev ? 1 : -1);
        }
        prevDateRef.current = selectedDateFromUrl;
    }, [selectedDateFromUrl, effectiveTodayStr, onDateChange]);

    const loadNews = useCallback(async (isInitial: boolean, page: number, date?: string, target: 'current' | 'prev' | 'next' = 'current') => {
        const dateKey = date || 'all';
        if (target === 'current') {
            if (isInitial) {
                if (newsCache.current[dateKey]) {
                    setNews(newsCache.current[dateKey].items);
                    setNextPage(newsCache.current[dateKey].nextPage);
                    setLoading(false);
                    return;
                } else {
                    if (date) {
                        setLoadingMore(true);
                        setLoading(false);
                        setNews(null);
                    } else {
                        setLoading(true);
                    }
                }
                window.scrollTo({ top: 0, behavior: 'auto' });
            }
            else setLoadingMore(true);
        } else {
            if (newsCache.current[dateKey]) {
                if (target === 'prev') setPrevNews(newsCache.current[dateKey].items);
                else setNextNews(newsCache.current[dateKey].items);
                return;
            }
        }
        
        try {
            const newsData = await fetchSupabaseNewsList(lang, page, INITIAL_BATCH_SIZE, date);
            const filteredResults = newsData.news.filter(n => n.source || n.user_id);
            
            if (target === 'current') {
                if (isInitial) {
                    setNews(filteredResults);
                    newsCache.current[dateKey] = { items: filteredResults, nextPage: newsData.nextPage };
                }
                else {
                    setNews(prev => {
                        const updatedNews = [...(prev || []), ...filteredResults];
                        if (page === 0) newsCache.current[dateKey] = { items: updatedNews, nextPage: newsData.nextPage };
                        return updatedNews;
                    });
                }
                setNextPage(newsData.nextPage);
            } else {
                if (target === 'prev') setPrevNews(filteredResults);
                else setNextNews(filteredResults);
                newsCache.current[dateKey] = { items: filteredResults, nextPage: newsData.nextPage };
            }
        } catch (e) {
            console.error(e);
        } finally {
            if (target === 'current') {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, [lang]);

    useEffect(() => { 
        if (isAuthLoading) return;
        const targetDate = selectedDateFromUrl || effectiveTodayStr;
        loadNews(true, 0, targetDate, 'current');

        // Preload adjacent dates
        const currentIndex = availableDates.findIndex(d => d.full === targetDate);
        if (currentIndex !== -1) {
            if (currentIndex < availableDates.length - 1) {
                loadNews(true, 0, availableDates[currentIndex + 1].full, 'prev');
            } else {
                setPrevNews([]);
            }
            if (currentIndex > 0) {
                loadNews(true, 0, availableDates[currentIndex - 1].full, 'next');
            } else {
                setNextNews([]);
            }
        }
    }, [selectedDateFromUrl, isAuthLoading, effectiveTodayStr, availableDates, loadNews]);

    useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && nextPage !== undefined && !loadingMore && !loading) {
                loadNews(false, nextPage, selectedDateFromUrl || undefined);
            }
        }, { threshold: 0.1 });
        if (observerTarget.current) observer.observe(observerTarget.current);
        return () => { if (observerTarget.current) observer.unobserve(observerTarget.current); };
    }, [nextPage, loadingMore, loading, loadNews, selectedDateFromUrl]);

    useEffect(() => {
        if (!loading && news && news.length > 0) {
            const hasShown = localStorage.getItem('newsSwipeTutorialShown');
            if (!hasShown) {
                setShowTutorial(true);
            }
        }
    }, [loading, news]);

    const handleCloseTutorial = () => {
        setShowTutorial(false);
        localStorage.setItem('newsSwipeTutorialShown', 'true');
    };

    const handleCreatePost = useCallback(() => {
        if (!user) {
            sessionStorage.setItem('redirectAfterLogin', '/feed/post');
            setLoginModalOpen(true);
            return;
        }
        window.navigateTo('/feed/post');
    }, [user]);

    const handleEdit = (id: string) => {
        window.navigateTo(`/feed/edit/${id}`);
    };

    const handleDeleteClick = (id: string) => {
        setActiveNewsId(id);
        setDeleteModalOpen(true);
    };

    const handleReportClick = (id: string) => {
        if (!user) {
            sessionStorage.setItem('redirectAfterLogin', window.getCurrentPath());
            setLoginModalOpen(true);
            return;
        }
        setActiveNewsId(id);
        setReportModalOpen(true);
    };

    const executeDelete = async () => {
        if (!activeNewsId || !news) return;
        setIsDeleting(true);
        try {
            const targetNews = news.find(n => n.id === activeNewsId);
            await deleteNewsPermanently(activeNewsId);
            if (targetNews && targetNews.user_id) {
                const { pointService } = await import('../../services/pointService');
                await pointService.deductPoints(targetNews.user_id, 10, 'News Feed Delete');
                showToast('info', t(lang, 'feedDeletePointDeducted'));
            }
            setNews(prev => prev ? prev.filter(n => n.id !== activeNewsId) : null);
            clearCache();
            showToast('success', t(lang, 'postDeletedSuccess'));
        } catch (e) {
            showToast('error', t(lang, 'failedToDelete'));
        } finally {
            setIsDeleting(false);
            setDeleteModalOpen(false);
            setActiveNewsId(null);
        }
    };

    const executeReport = async (reason: string) => {
        if (!activeNewsId || !user) return;
        if (!isValidUuid(activeNewsId)) {
            setReportModalOpen(false)
            showToast('info', t(lang, 'externalSourceReportError'))
            return
        }
        setIsReporting(true);
        try {
            const { error } = await supabase.from('news_reports').insert({ news_id: activeNewsId, reporter_id: user.id, reason: reason });
            if (error) {
                if (error.code === '23505') showToast('info', t(lang, 'alreadyReported'));
                else throw error;
            } else {
                await supabase.from('news_list').update({ is_reported: true }).eq('id', activeNewsId);
                showToast('success', t(lang, 'reportCompleted'));
            }
        } catch (e) {
            showToast('error', t(lang, 'mergeFailed'));
        } finally {
            setIsReporting(false);
            setReportModalOpen(false);
            setActiveNewsId(null);
        }
    };

    const handleDragEnd = (e: any, info: any) => {
        const threshold = 100;
        const currentDate = selectedDateFromUrl || effectiveTodayStr;
        const currentIndex = availableDates.findIndex(d => d.full === currentDate);

        if (info.offset.x > threshold && currentIndex > 0) {
            setDirection(-1);
            onDateChange(availableDates[currentIndex - 1].full);
            scrollToTop();
        } else if (info.offset.x < -threshold && currentIndex < availableDates.length - 1) {
            setDirection(1);
            onDateChange(availableDates[currentIndex + 1].full);
            scrollToTop();
        }
    };

    const renderNewsList = (items: NewsItem[] | null, isLoading: boolean) => {
        if (items && items.length > 0) {
            return (
                <div className="flex flex-col">
                    {items.map((item, idx) => (
                        <React.Fragment key={item.id}>
                            <FeedNewsCard item={item} index={idx} onClick={() => onNavigateToNews(item)} lang={lang} user={user} onEdit={handleEdit} onDelete={handleDeleteClick} onReport={handleReportClick} />
                            {(idx + 1) % 5 === 0 && <AdBanner slotId="7080513270" className="mx-4 my-2" />}
                        </React.Fragment>
                    ))}
                </div>
            );
        }
        if (items === null || isLoading || isAuthLoading) {
            return (
                <div className="py-40 flex flex-col items-center justify-center gap-4">
                    <div className="relative w-12 h-12">
                        <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-t-rose-500 rounded-full animate-spin"></div>
                    </div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] animate-pulse">{t(lang, 'gatheringIntel')}</p>
                </div>
            );
        }
        return (
            <div className="py-32 text-center flex flex-col items-center justify-center text-slate-300 px-10">
                <Newspaper size={48} className="opacity-20 mb-4" />
                <h3 className="text-slate-900 font-black uppercase text-sm mb-1">{t(lang, 'noIntelTitle')}</h3>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{t(lang, 'noIntelDesc')}</p>
            </div>
        );
    };

    return (
        <div className="bg-white min-h-screen relative">
            <SEO title={selectedDateFromUrl ? `${selectedDateFromUrl} ${t(lang, 'newsFeedSeoTitle')}` : t(lang, 'newsFeedSeoTitle')} description={t(lang, 'newsFeedSeoDesc')} />
            <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} onNavigate={onNavigateToNews} type="news" />
            <ConfirmModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} onConfirm={() => window.navigateTo('/login')} title={t(lang, 'loginRequiredTitle')} confirmLabel={t(lang, 'signInLabel')} />
            <ConfirmModal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={executeDelete} isLoading={isDeleting} title={t(lang, 'deleteFeed')} description={t(lang, 'deleteFeedConfirm')} confirmLabel={t(lang, 'delete')} />
            <ReportModal isOpen={reportModalOpen} onClose={() => setReportModalOpen(false)} onConfirm={executeReport} isLoading={isReporting} lang={lang} title={t(lang, 'reportFeedTitle')} />
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md px-4 h-14 flex items-center justify-between md:hidden">
                <div className="flex items-center gap-3"><XlevenLogo /></div>
                <button onClick={() => setIsSearchOpen(true)} className="p-2 text-slate-900"><Search size={20} /></button>
            </header>
            <div className="bg-white/95 backdrop-blur-md sticky top-[49px] z-30 md:top-0 shadow-sm border-b border-gray-100 px-4 py-2 flex items-center justify-between">
                <div className="flex-1 overflow-hidden">
                    <DateStrip dates={availableDates} selectedDate={selectedDateFromUrl || undefined} todayStr={effectiveTodayStr} onDateChange={(date) => { onDateChange(date); scrollToTop(); }} />
                </div>
            </div>
            <div className="max-w-[700px] mx-auto border-x border-gray-50 min-h-screen relative overflow-hidden bg-white">
                <motion.div
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    onDragEnd={handleDragEnd}
                    className="flex w-full touch-pan-y"
                >
                    <AnimatePresence initial={false} custom={direction} mode="popLayout">
                        <motion.div
                            key={(selectedDateFromUrl || effectiveTodayStr) + lang}
                            custom={direction}
                            initial={{ x: direction === 0 ? 0 : (direction > 0 ? '100%' : '-100%'), opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: direction > 0 ? '-100%' : '100%', opacity: 0 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="w-full shrink-0 bg-white relative"
                        >
                            {/* Current Panel */}
                            {renderNewsList(news, loading)}
                            
                            <div ref={observerTarget} className="h-40 flex flex-col items-center justify-center" />
                        </motion.div>
                    </AnimatePresence>
                </motion.div>
            </div>
            <div className="fixed bottom-[80px] md:hidden right-4 z-50">
                <button onClick={handleCreatePost} className="w-14 h-14 md:w-16 md:h-16 bg-rose-500 text-white rounded-full flex items-center justify-center shadow-2xl shadow-rose-200 md:hover:scale-110 active:scale-95 transition-all duration-300 ring-4 ring-white"><PenSquare className="w-6 h-6 md:w-7 md:h-7" strokeWidth={2.5} /></button>
            </div>
            {showTutorial && <SwipeTutorialOverlay onClose={handleCloseTutorial} />}
        </div>
    );
};
